import moment from "moment";
import React, { useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import filterFactory from "react-bootstrap-table2-filter";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import LottieLoader from '../../LottieLoader';
import lottieEmptyFile from '../../../files/animation/empty_new.json';
import lottieLoaderFile from '../../../files/animation/loading.json';
import UserName from "../../Reusable/UserName";
import { pasteValue } from "../../Reusable/OnPasteValue";
import NoReadingFilter, { ALL_VALUE } from "./NoReadingFilter";
import "./css/NoReadingModal.css";
const _ = require("lodash");
function fullname(cell, row) {
  return (
    <span className="patient-name-link" title="Click to view patient dashboard">
      <UserName user={row} />
      <div className="patient-name-dob">
        <i className="mdi mdi-cake-variant"></i>
        {row.bday ? moment(row.bday).format("MMMM DD, YYYY") : "-"}
      </div>
    </span>
  );
}

const EXPECTATION_TAG_CLASS = {
  "MVV Adherence": "expectation-tag--mvv",
  "ANS Adherence": "expectation-tag--ans",
  "Care Provider Adherence": "expectation-tag--care-provider",
  "CCM Only": "expectation-tag--ccm-only",
  "Adherence on Hold": "expectation-tag--on-hold",
  "No Adherence Logic": "expectation-tag--none"
};

function getExpectationTagClass(value) {
  return EXPECTATION_TAG_CLASS[value] || "expectation-tag--none";
}

function expectationFormatter(cell, row) {
  const value = row.expectationsToFollow ? row.expectationsToFollow : "No Adherence Logic";
  return (
    <span className={`expectation-tag ${getExpectationTagClass(value)}`}>
      {value}
    </span>
  );
}

const PROGRAM_TAG_CLASS = {
  "": "program-tag--none",
  "CCM": "program-tag--ccm",
  "RPM": "program-tag--rpm",
  "CCM&RPM": "program-tag--combo"
};

const PROGRAM_TAG_LABEL = {
  "": "NONE",
  "CCM": "CCM",
  "RPM": "RPM",
  "CCM&RPM": "CCM & RPM"
};

function getProgramTagClass(value) {
  return PROGRAM_TAG_CLASS[value] !== undefined ? PROGRAM_TAG_CLASS[value] : "program-tag--other";
}

function getProgramTagLabel(value) {
  return PROGRAM_TAG_LABEL[value] !== undefined ? PROGRAM_TAG_LABEL[value] : value;
}

function programFormatter(cell, row) {
  const rawValue = row.enrolledPrograms || "";
  return (
    <span className={`program-tag ${getProgramTagClass(rawValue)}`}>
      {getProgramTagLabel(rawValue)}
    </span>
  );
}

const defaultSorted = [
  {
    dataField: "lname", // if dataField is not match to any column you defined, it will be ignored.
    order: "asc" // desc or asc
  }
];

function formatPhone(cell, row) {
  return <div>{cell ? cell : "-"}</div>;
}
const MyExportCSV = (props) => {
  const handleClick = () => {
    props.onExport();
  };
  return (
    <button type="button" className="patient-list-export-btn download-click-cls" onClick={handleClick}>
      <span className="patient-list-export-icon">
        <i className="mdi mdi-download"></i>
      </span>
      <span>Export to CSV</span>
    </button>
  );
};
const MySearch = (props) => {
  let input;
  const [hasValue, setHasValue] = useState(false);
  const handleClick = () => {
    const value = (input.value || "").trim();
    setHasValue(!!value);
    props.onSearch(value);
  };
  const handleClear = () => {
    input.value = "";
    setHasValue(false);
    props.onSearch("");
  };
  return (
    <div className="patient-list-search-shell">
      <span className="patient-list-search-icon">
        <i className="mdi mdi-magnify"></i>
      </span>
      <input
        type="text"
        className="patient-list-search-input"
        id="exampleInputName1Test"
        placeholder="Search Last Name, First Name, Nickname, Email, etc..."
        ref={(n) => (input = n)}
        onChange={handleClick}
        onPaste={(e) => {
          e.preventDefault();
          const event = pasteValue(e)
          handleClick({ target: { value: event.target.value } });
        }}
      />
      {hasValue && (
        <button type="button" className="patient-list-search-clear" onClick={handleClear} aria-label="Clear search">
          <i className="mdi mdi-close-circle"></i>
        </button>
      )}
    </div>
  );
};
function getCareProviderLabel(user) {
  return user && user.mainCareProvider
    ? `${user.mainCareProvider.lname}, ${user.mainCareProvider.fname}`
    : "";
}

function buildOptionsFromValues(values) {
  return Array.from(new Set(values.filter((value) => !!value)))
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value, label: value }));
}

const NoReading = (props, context) => {
  const [enrolledProgramFilter, setEnrolledProgramFilter] = useState(ALL_VALUE);
  const [expectationFilter, setExpectationFilter] = useState(ALL_VALUE);
  const [careProviderFilter, setCareProviderFilter] = useState(ALL_VALUE);
  const [organizationFilter, setOrganizationFilter] = useState(ALL_VALUE);
  const allUsers = props.usersGivenAccessMyReading || [];
  const careProviderOptions = buildOptionsFromValues(allUsers.map(getCareProviderLabel));
  const organizationOptions = buildOptionsFromValues(allUsers.map((user) => user.organization));
  const filteredUsers = allUsers
    .filter((user) => {
      const matchesProgram =
        enrolledProgramFilter === ALL_VALUE || (user.enrolledPrograms || "") === enrolledProgramFilter;
      const matchesExpectation =
        expectationFilter === ALL_VALUE || (user.expectationsToFollow || "") === expectationFilter;
      const matchesCareProvider =
        careProviderFilter === ALL_VALUE || getCareProviderLabel(user) === careProviderFilter;
      const matchesOrganization =
        organizationFilter === ALL_VALUE || (user.organization || "") === organizationFilter;
      return matchesProgram && matchesExpectation && matchesCareProvider && matchesOrganization;
    })
    .sort((a, b) => {
      const lnameCompare = (a.lname || "").localeCompare(b.lname || "");
      if (lnameCompare !== 0) return lnameCompare;
      return (a.fname || "").localeCompare(b.fname || "");
    });
  const isFilterActive =
    enrolledProgramFilter !== ALL_VALUE ||
    expectationFilter !== ALL_VALUE ||
    careProviderFilter !== ALL_VALUE ||
    organizationFilter !== ALL_VALUE;
  const resetFilters = () => {
    setEnrolledProgramFilter(ALL_VALUE);
    setExpectationFilter(ALL_VALUE);
    setCareProviderFilter(ALL_VALUE);
    setOrganizationFilter(ALL_VALUE);
  };
  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      {"   "}Showing {from} to {to} of {size} Results
    </span>
  );
  const options = {
    paginationSize: 4,
    pageStartIndex: 1,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    nextPageTitle: "First page",
    prePageTitle: "Pre page",
    firstPageTitle: "Next page",
    lastPageTitle: "Last page",
    showTotal: true,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      {
        text: "10",
        value: 10
      },
      {
        text: "20",
        value: 20
      },
      {
        text: "All",
        value: filteredUsers.length
      }
    ] // A numeric array is also available. the purpose of above example is custom the text
  };

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      window.location.href = `/dashboard/${row.fname
        .replace(/\s+/g, "-").replace(/[^a-zA-Z0-9 ]/g, '')
        .toLowerCase()}-${row.lname.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9 ]/g, '').toLowerCase()}/${
        row._id
      }`;
    }
  };

  function priceFormatter(column, colIndex, { sortElement, filterElement }) {
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span className="mb-2">{filterElement}</span>  
        {column.text}
        {sortElement}
      </div>
    );
  }
  const NoDataIndication = () => (
    <div>
      {props.loading && (
        <LottieLoader
          loadLoop={true}
          autoplayLoop={true}
          source={lottieLoaderFile}
          Lheight={150}
          Lwidth={150}
          subTitle={"Please Wait ..."}
        />
      )}
      {!props.loading && (
        <LottieLoader
          loadLoop={false}
          autoplayLoop={true}
          source={lottieEmptyFile}
          Lheight={300}
          Lwidth={300}
          title={"Sorry! No patient found :("}
          subTitle={"We can't find any patient."}
        />
      )}              
    </div>
  );
  return (
    <ToolkitProvider
      key={`${enrolledProgramFilter}-${expectationFilter}-${careProviderFilter}-${organizationFilter}`}
      keyField="_id"
      data={filteredUsers}
      columns={[
        {
          dataField: "lname",
          text: "Last, First Name",
          formatter: fullname,
          sort: true,
          searchable:true,
          csvFormatter: (cell, row) => {
            return (row.lname ? _.upperFirst(row.lname) : "") + ", " + (row.fname ? _.upperFirst(row.fname) : "");
          },
          headerFormatter: priceFormatter,
        },
        {
          dataField: "isActive",
          text: "Active/Inactive",
          hidden: true,
          csvFormatter: (cell, row) => {
            return row.isActive ? "Active" : "Inactive";
          }
        },
        {
          dataField: "bday",
          text: "DOB",
          hidden: true,
          csvFormatter: (cell, row) => {
            return row.bday ? moment(row.bday).format("MMMM DD, YYYY") : "-";
          }
        },
        {
          dataField: "email",
          text: "Email Address",
          sort: true,
          searchable:true,
          csvFormatter: (cell, row) => {
            return row.email;
          }
        },
        {
          dataField: "phone",
          text: "Phone",
          searchable:true,
          sort: true,
          formatter: formatPhone,
          csvFormatter: (cell, row) => {
            return row.phone;
          }
        },
        {
          dataField: "username",
          text: "Username",
          searchable:true,
          csvFormatter: (cell, row) => {
            return row.username;
          },
          sort: true
        },
        {
          dataField: "createdAt",
          text: "Enrolled Date",
          sort: true,
          formatter: (cell, row) => {
            return moment(row.createdAt).format("MMMM DD, YYYY");
          },
          headerFormatter: priceFormatter,
          csvFormatter: (cell, row, rowIndex) => {
            return moment(row.createdAt).format("MMMM DD, YYYY");
          },        
        },
        {
          dataField: "deviceReadingDate",
          text: "Last Upload Date",
          sort: true,
          searchable:true,
          formatter: (cell, row) => {
            return (row.deviceReadingDate?moment(row.deviceReadingDate).format("MMMM DD, YYYY") :"NONE");
          },
          csvFormatter: (cell, row) => {
            return (row.deviceReadingDate?moment(row.deviceReadingDate).format("MMMM DD, YYYY") :"NONE");
          },
        },
        {
          dataField: "lastNoteDate",
          text: "Date of Last Note",
          sort: true,
          searchable: true,
          formatter: (cell, row) => {
            if (cell && row && row.lastNote) {
              const note = row.lastNote;
              return (
                <div>
                  <span>{moment(note.createdAt).format("MMMM DD, YYYY hh:mm A")}</span>
                  <span style={{ fontWeight: 800 }}>
                    <br></br>
                    <br></br>
                    {note.subject ? note.subject : <div className='text-center'>-</div>}
                  </span>
                </div>
              );
            } else {
              return <div className="text-center">-</div>;
            }
          },
          csvFormatter: (cell, row) => {
            const date = row.lastNote && row.lastNote.createdAt
              ? moment(row.lastNote.createdAt).format("MMMM DD, YYYY")
              : "NONE";
            const subject = row.lastNote && row.lastNote.subject ? row.lastNote.subject : "NONE";
            return `${date} | Subject: ${subject}`;
          },
        },
        {
          dataField: "organization",
          text: "Organization",
          sort: true,
          searchable:true,
          formatter: (cell, row) => <div>{cell ? cell : "-"}</div>,
          csvFormatter: (cell, row) => {
            return row.organization ? row.organization : "-";
          }
        },
        {
          dataField: "mainCareProvider",
          text: "Main Care Provider",
          sort: true,
          searchable:true,          
          headerFormatter: priceFormatter,
          formatter: (cell, row) => {
            return row.mainCareProvider
              ? row.mainCareProvider.lname + ", " + row.mainCareProvider.fname
              : "-";
          },
          csvFormatter: (cell, row) => {
            return row.mainCareProvider
              ? row.mainCareProvider.lname + ", " + row.mainCareProvider.fname
              : "-";
          },   
          filterValue: (cell, row) => {
            return row.mainCareProvider
            ? row.mainCareProvider.lname + ", " + row.mainCareProvider.fname
            : "";
          },       
        },
        {
          dataField: "enrolledPrograms",
          text: "Enrolled Program(s)",
          sort: true,
          searchable:true,
          formatter: programFormatter,
          csvFormatter: (cell, row) => {
            return getProgramTagLabel(row.enrolledPrograms || "");
          }
        },
        {
          dataField: "expectationsToFollow",
          text: "Client Expectation to Follow",
          sort: true,
          searchable:true,
          formatter: expectationFormatter,
          csvFormatter: (cell, row) => {
              return row.expectationsToFollow ? row.expectationsToFollow : 'No Adherence Logic';
          }
        }
      ]}
      search 
      exportCSV={{ fileName: "NoReadingsPast6Days.csv" }}
    >
      {(toolKitProp) => (
        <div>
          <div className="patient-list-modal-subtitle">
            <div className="cpt-patient-list-info">
              <div className="cpt-patient-list-title">Patients with No Readings (Past 6 Days)</div>
              <div className="cpt-patient-list-desc">
                Patients who haven&apos;t uploaded a vital reading in the last 6 or more days.
              </div>
            </div>
            <div className="cpt-patient-list-meta">
              <span>
                <i className="mdi mdi-account-group"></i>
                {filteredUsers.length} of {allUsers.length} Patients
              </span>
            </div>
          </div>
          <NoReadingFilter
            enrolledProgramFilter={enrolledProgramFilter}
            expectationFilter={expectationFilter}
            careProviderFilter={careProviderFilter}
            organizationFilter={organizationFilter}
            careProviderOptions={careProviderOptions}
            organizationOptions={organizationOptions}
            onEnrolledProgramChange={setEnrolledProgramFilter}
            onExpectationChange={setExpectationFilter}
            onCareProviderChange={setCareProviderFilter}
            onOrganizationChange={setOrganizationFilter}
            onReset={resetFilters}
            isFilterActive={isFilterActive}
          />
          <div className="patient-list-toolbar">
            <MySearch {...toolKitProp.searchProps} />
            <MyExportCSV {...toolKitProp.csvProps} />
          </div>
          <div className="scrollbar-table-cls">
          <BootstrapTable
            wrapperClasses="table-responsive"
            {...toolKitProp.baseProps}
            noDataIndication={ () => <NoDataIndication /> }
            defaultSorted={defaultSorted}
            pagination={paginationFactory(options)}
            filter={filterFactory()}
            rowEvents={rowEvents}
            rowClasses="patient-list-row-animate"
            {...toolKitProp.baseProps}
            rowStyle={{ cursor: "pointer" }}
          />
          </div>
        </div>
      )}
    </ToolkitProvider>
  );
};

export default NoReading;
